/**
 * Formats a date to "DD MMM YYYY" format (e.g., "15 Jan 2025")
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
function formatEventDate(date: string | Date): string {
  const dateObj = typeof date === "string" ? new Date(date) : date

  const day = dateObj.getDate()
  const month = dateObj.toLocaleString("en-US", { month: "short" })
  const year = dateObj.getFullYear()

  return `${day} ${month} ${year}`
}

/**
 * Extracts the first sentence from a text
 * @param text - Full text
 * @returns First sentence or empty string if no text
 */
export function extractFirstSentence(text: string | null | undefined): string {
  if (!text || text.trim().length === 0) {
    return ""
  }

  // Match the first sentence (up to first period, exclamation, or question mark followed by space or end of string)
  const match = text.match(/^[^.!?]+[.!?](?:\s|$)/)

  if (match) {
    return match[0].trim()
  }

  // If no sentence ending found, check if there's a newline
  const newlineIndex = text.indexOf("\n")
  if (newlineIndex > 0) {
    return text.substring(0, newlineIndex).trim()
  }

  // If text is short (less than 160 chars), return it as is
  if (text.length < 160) {
    return text.trim()
  }

  // Otherwise, return first 160 characters
  return `${text.substring(0, 160).trim()}...`
}

/**
 * Generates event page title for meta tags
 * @param eventName - Name of the event
 * @param eventDate - Start date of the event
 * @param communityName - Name of the community
 * @returns Formatted title string
 */
export function generateEventMetaTitle(
  eventName: string,
  eventDate: string | Date,
  communityName: string,
): string {
  const formattedDate = formatEventDate(eventDate)
  return `${eventName}, ${formattedDate}, ${communityName} - WERZ`
}

/**
 * Generates community page title for meta tags
 * Format: [community name], WERZ
 * @param communityName - Name of the community
 * @returns Formatted title string
 */
export function generateCommunityMetaTitle(communityName: string): string {
  return `${communityName}, WERZ`
}
